import React, { useRef, useState, useEffect } from 'react';
import './VerifyCodePanel.css';
import VerificationCodeInput, { VerificationCodeCountdown } from './VerificationCodeInput';
import Config from '../utils/Config';
import {
    displayMessage,
} from '../utils/functions';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const InlineVerificationCode = ({
    onSubmit,
    onResend,
    messageClass,
    messageRef,
    emailInputRef,
    codeExpiresAt,
    setExpiresAt,
    setPendingMessage,
    setMessageClass,
    setPendingStyle,
    email,
    isLockedOut = false,
    parent,
    userId,
    mfaAction,
    mfaChallengeId,
    lockoutUntil,
    pendingMessage,
    onClose,
    autoSubmit = true,
    allowResend = true,
    busy = false,
    errorMessage,
}) => {
    const [code, setCode] = useState(Array(6).fill(''));
    const inputsRef = useRef([]);
    const [timeLeft, setTimeLeft] = useState("");
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [inputsDisabled, setInputsDisabled] = useState(false);
    const [lockRemaining, setLockRemaining] = useState('');

    const isSixDigitsEntered = code.every((d) => d !== '');
    const hasError = messageClass === 'warning' || messageClass === 'error';

    const handleSubmit = async () => {
        if (!isSixDigitsEntered || busy) return;
        setIsSubmitting(true);
        try {
            await onSubmit(code.join(''));
        } finally {
            setIsSubmitting(false);
        }
    };

    useEffect(() => {
        inputsRef.current[0]?.focus();
    }, []);

    useEffect(() => {
        displayMessage(messageRef, pendingMessage, "success", setMessageClass);
    }, [pendingMessage]);

    useEffect(() => {
        if (errorMessage) displayMessage(messageRef, errorMessage, 'warning', setMessageClass);
    }, [errorMessage, messageRef, setMessageClass]);

    // Lockout handling: disable inputs and clear values immediately
    useEffect(() => {
        if (isLockedOut) {
            setInputsDisabled(true);
            setCode(Array(6).fill(''));
            inputsRef.current.forEach((input) => { if (input) input.value = ''; });
        } else {
            // Lockout finished -> re-enable and focus first box on next paint
            setInputsDisabled(false);
            const focusFirst = () => inputsRef.current[0]?.focus();
            if (typeof requestAnimationFrame === 'function') requestAnimationFrame(focusFirst);
            else setTimeout(focusFirst, 0);
        }
    }, [isLockedOut]);

    useEffect(() => {
        if (!isLockedOut || !lockoutUntil) { setLockRemaining(''); return; }
        const end = typeof lockoutUntil === 'number' ? lockoutUntil : new Date(lockoutUntil).getTime();
        const tick = () => {
            const diff = Math.max(0, Math.floor((end - Date.now()) / 1000));
            const m = String(Math.floor(diff / 60)).padStart(2, '0');
            const s = String(diff % 60).padStart(2, '0');
            setLockRemaining(`${m}:${s}`);
        };
        tick();
        const id = setInterval(tick, 1000);
        return () => clearInterval(id);
    }, [isLockedOut, lockoutUntil]);

    // Timer logic
    useEffect(() => {
        if (!codeExpiresAt) return;

        let intervalId;

        const updateTimer = () => {
            const now = new Date();
            const expires = new Date(codeExpiresAt);
            // const expires = new Date(now);
            const diff = Math.max(0, Math.floor((expires - now) / 1000));

            if (diff <= 0) {
                clearInterval(intervalId);
                setInputsDisabled(true); // ← NEW: disable on expiry

                // 🔹 Clear all 6 inputs (state + DOM values)
                setCode(Array(6).fill(''));
                inputsRef.current.forEach((input) => {
                    if (input) input.value = '';
                });

                setTimeLeft(allowResend ? `
                    ⏳ Code expired. 
                    <button class="resend-button" onclick="window.__resendCode && window.__resendCode()">Resend Code</button>
                ` : '⏳ Code expired. Cancel and request a new code.');
                displayMessage(messageRef, allowResend ? "❌ Code expired. Click 'Resend Code'." : '❌ Code expired. Cancel and request a new code.', "warning", setMessageClass);
            } else {
                const minutes = Math.floor(diff / 60);
                const seconds = diff % 60;
                setTimeLeft(
                    `⏳ Code expires in <span class="emailMessageHilite">${minutes
                        .toString()
                        .padStart(2, '0')}:${seconds
                            .toString()
                            .padStart(2, '0')}</span>`
                );
            }
        };

        updateTimer();
        intervalId = setInterval(updateTimer, 1000);

        return () => clearInterval(intervalId);
    }, [codeExpiresAt, allowResend]);

    useEffect(() => {
        if (!inputsDisabled) {
            const el = inputsRef.current[0];
            if (el) {
                el.focus();
                // el.select?.(); // optional
            }
        }
    }, [inputsDisabled]);

    const resendFunction = async (endpoint) => {
        try {
            const resent = true;
            const response = await fetch(`${Config.API.API_BASE_URL}${endpoint}`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
                body: JSON.stringify(
                    parent === 'update_email' ? { userId, email, resent, challengeId: mfaChallengeId, mfaAction } : { email, resent, challengeId: mfaChallengeId, mfaAction },
                )
            });

            const data = await response.json().catch(() => ({}));
            
            if (response.ok && data.expiresAt) {
                // ✅ Clear inputs
                setCode(Array(6).fill(''));
                inputsRef.current.forEach((input) => {
                    if (input) input.value = '';
                });

                // ✅ Reset expiry and re-enable inputs
                setExpiresAt(new Date(data.expiresAt));
                setInputsDisabled(false);

                // ✅ Focus first input AFTER re-render (when it's no longer disabled)
                const focusFirst = () => inputsRef.current[0]?.focus();
                if (typeof requestAnimationFrame === 'function') {
                    requestAnimationFrame(focusFirst);
                } else {
                    setTimeout(focusFirst, 0);
                }

                setPendingMessage("✅ A new verification code was sent.");
                setMessageClass("success");
                setPendingStyle("success");
                displayMessage(messageRef, data.message || "✅ A new verification code was sent.", "success", setMessageClass);
            } else {
                setPendingMessage("❌ Failed to resend code.");
                setMessageClass("warning");
                setPendingStyle("warning");
                displayMessage(messageRef, data.message || "❌ Failed to resend code.", "warning", setMessageClass);
            }
        } catch (err) {
            setPendingMessage("❌ Error contacting server.");
            setMessageClass("warning");
            setPendingStyle("warning");
            displayMessage(messageRef, "❌ Error contacting server.", "warning", setMessageClass);
        }
    }

    const handleResend = async () => {
        if (!allowResend) return;
        let endpoint;

        switch (parent) {
            case 'login':
                endpoint = Config.ENDPOINTS.USER_LOGIN_MFA_RESEND;
                break;
            case 'update_email':
                endpoint = Config.ENDPOINTS.USER_UPDATE_EMAIL_INIT;
                break;
            case 'mfa':
                endpoint = mfaAction === 'enable'
                    ? Config.ENDPOINTS.USER_MFA_RESEND_ENABLE
                    : Config.ENDPOINTS.USER_MFA_RESEND_DISABLE;
                break;
            case 'approve-claim':
            case 'cancel-claim':
                endpoint = Config.ENDPOINTS.ADMIN_CLAIM_RESEND;
                break;
            default:
                endpoint = Config.ENDPOINTS.USER_REGISTER;
                break;
        }

        resendFunction(endpoint);
    };

    useEffect(() => {
        if (!allowResend) return;
        window.__resendCode = handleResend;
        return () => delete window.__resendCode;
    }, [handleResend, allowResend]);

    useEffect(() => {
        const handleDocumentClick = (e) => {
            if (inputsDisabled || busy) return; // ← NEW: don’t refocus while disabled
            const isClickInsideInput = inputsRef.current.some(
                (input) => input && input.contains(e.target)
            );

            if (!isClickInsideInput) {
                inputsRef.current[0]?.focus();
            }
        };

        document.addEventListener("click", handleDocumentClick);
        return () => document.removeEventListener("click", handleDocumentClick);
    }, [inputsDisabled, busy]); // ← NEW dep

    return (
        <div className="verify-code-wrapper">
            {
                parent !== 'login' &&
                parent !== 'update_email' &&
                parent !== 'approve-claim' &&
                parent !== 'set-user-active' &&
                parent !== 'set-user-inactive' &&
                parent !== 'cancel-claim' &&
                parent !== 'account-management' ? 
            (
                <h2>
                    <span className="titleIcon"><FontAwesomeIcon icon="envelope-circle-check" /></span>&nbsp;Verify Your Email
                </h2>
            ) : null}

            {   
                parent !== 'login' && 
            (
                <div ref={messageRef} className={messageClass} id="message" />
            )}

            <p>Enter the 6-digit code we emailed you.</p>
            <VerificationCodeInput value={code} onChange={setCode} inputsRef={inputsRef}
                disabled={inputsDisabled || busy} onInput={() => setIsSubmitting(true)} onComplete={autoSubmit ? onSubmit : undefined} />

            {/* {isSixDigitsEntered && !isSubmitting && !hasError && ( */}
                <button
                    type="submit"
                    onClick={handleSubmit}
                    className="submit-mfa-button"
                    disabled={inputsDisabled || busy}
                >
                    <span className="titleIcon"><FontAwesomeIcon icon="key" /></span>&nbsp;Submit Code
                </button>
            {/* )} */}

            {isLockedOut ? (
                <VerificationCodeCountdown>
                    ⏳ Too many attempts. Try again in{" "}
                    <span className="emailMessageHilite">{lockRemaining || '00:00'}</span>
                </VerificationCodeCountdown>
            ) : (
                <VerificationCodeCountdown html={timeLeft} />
            )}
            <span className="emailMessage">
                <p>Code has been sent to: <span className="emailMessageHilite">{email}</span></p>
            </span>      

            <div className="modal-actions login-code-panel">
              <button
                type="button"
                className="cancel-verify-btn"
                disabled={busy}
                onClick={() => {
                  onClose(true);
                }}
              >
                Cancel
              </button>
            </div>      
        </div>
    );
};

export default InlineVerificationCode;